import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Link, Navigate, useNavigate, } from 'react-router-dom';
import { 
  CssBaseline, ThemeProvider, Button, Typography, Container, TextField, 
  Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, 
  IconButton, AppBar, Toolbar, Menu, MenuItem, useMediaQuery, Box,
  Avatar, Chip, Divider, ListItemIcon, ListItemText, Card, Grid, CardContent,
  CardActions, Fade, Grow, Drawer, SwipeableDrawer, Hidden, Alert, CircularProgress
} from '@mui/material';
import { 
  Visibility, VisibilityOff, Menu as MenuIcon, 
  VerifiedUser, PersonAdd, Edit, Assessment, 
  Logout, Login, Business, AccountCircle,
  Dashboard, AddBox, NotificationsNone, Search, ArrowForward, Category,
  Security, Analytics, ManageAccounts, Close as CloseIcon,
  Lock, Warning , ExitToApp  
} from '@mui/icons-material';
 
// Import components
import Registro from './components/Registro';
import RegistroModular from './components/Registro_Modular';
import Verificar from './components/Verificar';
import MatrizRepresentante from './components/Matrizrepresentante';
import Reporteria from './components/Reporteria';
import theme from './components/theme';
import Sumatoria from './components/Sumatoria'; 
import Categorias from './components/Categoria';
import ReporteAgrupado from './components/ReporteAgrupado';
import HistoricoRepresentantes from './components/HistoricoRepresentantes';

// Import auth components
import { AuthProvider, useAuth } from './hooks/useAuth';
import NavigationMenu from './components/NavigationMenu';

// Responsive breakpoints helper
const useResponsive = () => {
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  return { isMobile, isTablet, isDesktop, isSmallMobile };
};

// Protected Route Component with SubModule validation
const ProtectedRoute = ({ 
  children, 
  subModuleId = null,
  moduleId = null,
  fallbackPath = '/'
}) => {
  const { isAuthenticated, loading, hasSubModuleAccess, hasModuleAccess } = useAuth();

  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '50vh' 
        }}
      >
        <CircularProgress sx={{ mb: 2 }} />
        <Typography>Verificando permisos...</Typography>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // Verificar acceso a submódulo específico
  if (subModuleId && !hasSubModuleAccess(subModuleId)) {
    return <UnauthorizedAccess />;
  }

  // Verificar acceso a módulo específico
  if (moduleId && !hasModuleAccess(moduleId)) {
    return <UnauthorizedAccess />;
  }

  return children;
};

// Unauthorized Access Component
const UnauthorizedAccess = () => (
  <Container maxWidth="sm" sx={{ textAlign: 'center', py: 8 }}>
    <Warning sx={{ fontSize: 80, color: '#ff9800', mb: 2 }} />
    <Typography variant="h4" sx={{ mb: 2, color: '#432918' }}>
      Acceso No Autorizado
    </Typography>
    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
      No tienes permisos para acceder a esta funcionalidad. 
      Contacta con el administrador del sistema.
    </Typography>
    <Button 
      component={Link} 
      to="/" 
      variant="contained"
      sx={{ bgcolor: '#432918' }}
    >
      Volver al Inicio
    </Button>
  </Container>
);

// No Access Component - Para cuando no hay sesión del portal
const NoAccessComponent = () => (
  <Container maxWidth="sm" sx={{ textAlign: 'center', py: 8 }}>
    <Security sx={{ fontSize: 80, color: '#432918', mb: 2 }} />
    <Typography variant="h4" sx={{ mb: 2, color: '#432918' }}>
      Portal de Acceso Requerido
    </Typography>
    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
      Esta aplicación requiere acceso desde el portal principal. 
      Por favor, accede desde el portal de aplicaciones.
    </Typography>
    <Button 
      href={process.env.REACT_APP_PORTAL_URL}
      variant="contained"
      sx={{ bgcolor: '#432918' }}
    >
      Ir al Portal
    </Button>
  </Container>
);
const Home = () => {
  const { isAuthenticated, user, hasActionPermission } = useAuth();
  const navigate = useNavigate();
  
  // Hook debe ir ANTES de cualquier return condicional
  React.useEffect(() => {
    if (isAuthenticated && hasActionPermission(17, 'LISTAR')) {
      navigate('/verificar');
    }
  }, [isAuthenticated, hasActionPermission, navigate]);
  
  if (!isAuthenticated) {
    return <NoAccessComponent />;
  }
  
  // Página de respaldo si no tiene acceso a verificar
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ color: '#432918' }}>
        <Business sx={{ mr: 2, verticalAlign: 'bottom' }} />
        Sistema GREMIALES
      </Typography>
      
      <Typography variant="h6" color="text.secondary" gutterBottom>
        Bienvenido, {user?.nombre || user?.usuario}
      </Typography>

      <Typography variant="body1" sx={{ mt: 3 }}>
        Utiliza el menú de navegación para acceder a los módulos disponibles.
      </Typography>
    </Container>
  );
};

// Responsive Navigation Drawer Component
const NavigationDrawer = ({ open, onClose }) => {
  const { isMobile } = useResponsive();
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) return null;

  return (
    <SwipeableDrawer
      anchor="left"
      open={open}
      onClose={onClose}
      onOpen={() => {}}
      PaperProps={{
        sx: { 
          width: { xs: '85%', sm: 300 },
          maxWidth: 350,
          bgcolor: '#ffffff'
        }
      }}
      ModalProps={{
        keepMounted: true
      }}
    >
      <Box sx={{ 
        p: 3, 
        borderBottom: '1px solid #e0e0e0',
        bgcolor: '#432918',
        color: 'white'
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            GREMIALES
          </Typography>
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      <NavigationMenu onItemClick={onClose} />
    </SwipeableDrawer>
  );
};

const AppContent = () => {
  const { isAuthenticated, user, logout, loading } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const { isMobile, isTablet, isDesktop, isSmallMobile } = useResponsive();

  const handleLogout = () => {
    logout();
    setDrawerOpen(false);
  };

  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          bgcolor: '#f8f5f2'
        }}
      >
        <CircularProgress sx={{ mb: 2, color: '#432918' }} size={48} />
        <Typography variant="h6" color="#432918">
          Verificando sesión del portal...
        </Typography>
      </Box>
    );
  }

  // 🔒 BLOQUEO TOTAL - Solo usuarios autenticados del portal
  if (!isAuthenticated) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <NoAccessComponent />
      </ThemeProvider>
    );
  }

  // ✅ Usuario autenticado - Mostrar aplicación completa
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <div className="App">
        {/* AppBar solo para usuarios autenticados */}
        <AppBar 
          position="static" 
          elevation={0}
          sx={{
            backgroundColor: '#FFFFFF',
            borderBottom: '1px solid #e0e0e0',
            boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
            position: 'sticky',
            top: 0,
            zIndex: 1100
          }}
        >
          <Toolbar sx={{ 
            py: { xs: 1, sm: 1.5 }, 
            px: { xs: 2, sm: 3 },
            justifyContent: 'space-between',
            minHeight: { xs: 56, sm: 64 }
          }}>
            {/* Logo/Brand Section */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              minWidth: { xs: 'auto', sm: 280 },
              flex: { xs: 1, md: 'none' }
            }}>
              <Card
                elevation={0}
                sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  bgcolor: '#432918',
                  color: '#FFFFFF',
                  borderRadius: { xs: 2, md: 3 },
                  px: { xs: 1.5, sm: 2 },
                  py: { xs: 0.5, sm: 1 },
                  mr: { xs: 1, sm: 3 },
                  cursor: 'pointer'
                }}
                onClick={() => navigate('/')}
              >
                <Business sx={{ 
                  fontSize: { xs: 20, sm: 24 }, 
                  mr: { xs: 1, sm: 1.5 } 
                }} />
                <Box>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 700,
                      lineHeight: 1,
                      fontSize: { xs: '0.9rem', sm: '1.1rem' }
                    }}
                  >
                    GREMIALES
                  </Typography>
                </Box>
              </Card>
            </Box>

          <Hidden mdDown>
  <Box sx={{ 
    display: 'flex', 
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center'
  }}>
    <NavigationMenu />
  </Box>
</Hidden>

            {/* User Section */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              minWidth: { xs: 'auto', sm: 200 },
              justifyContent: 'flex-end'
            }}>
              {/* User Info */}
              <Hidden smDown>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mr: 2,
                  bgcolor: '#f8f5f2',
                  px: 2,
                  py: 1,
                  borderRadius: 2
                }}>
                  <AccountCircle sx={{ mr: 1, color: '#432918' }} />
                  <Box>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 600,
                        color: '#432918',
                        lineHeight: 1
                      }}
                    >
                      {user?.nombre || 'Usuario'}
                    </Typography>
                    {user?.isAdmin && (
                      <Chip 
                        label="Admin" 
                        size="small" 
                        color="error"
                        sx={{ 
                          height: 16, 
                          fontSize: '0.7rem',
                          mt: 0.5
                        }}
                      />
                    )}
                  </Box>
                </Box>
              </Hidden>

          <Hidden smDown>
  <Button
    onClick={handleLogout}
    startIcon={<ExitToApp />}
    variant="outlined"
    size="small"
    sx={{
      borderColor: '#432918',
      color: '#432918',
      fontWeight: 500,
      textTransform: 'none',
      px: 2.5,
      borderRadius: 2,
      '&:hover': {
        borderColor: '#5a3a24',
        bgcolor: '#432918',
        color: 'white',
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(67, 41, 24, 0.25)'
      },
      transition: 'all 0.3s ease'
    }}
  >
    Volver al Portal
  </Button>
</Hidden>

              {/* Mobile Menu Button */}
              <Hidden mdUp>
                <IconButton
                  onClick={() => setDrawerOpen(true)}
                  sx={{ 
                    color: '#666666',
                    backgroundColor: '#f5f5f5',
                    p: { xs: 1, sm: 1.5 },
                    '&:hover': {
                      backgroundColor: '#e0e0e0',
                      color: '#432918'
                    }
                  }}
                >
                  <MenuIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                </IconButton>
              </Hidden>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Responsive Navigation Drawer */}
        <NavigationDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
        />

        {/* Main Content Container */}
        <Container 
          maxWidth="lg" 
          sx={{ 
            mt: { xs: 1, sm: 2 },
            px: { xs: 1, sm: 2, md: 3 },
            pb: { xs: 2, sm: 3 },
            minHeight: 'calc(100vh - 100px)'
          }}
        >
  <Routes>
  {/* Ruta principal - Home (SOLO AUTENTICADOS) */}
  <Route path="/" element={<Home />} />
  <Route path="/dashboard" element={<Home />} />
  
  {/* TODAS las rutas requieren autenticación */}
  <Route path="/verificar" element={
    <ProtectedRoute subModuleId={17}>
      <Verificar />
    </ProtectedRoute>
  } />
  
  <Route path="/registro" element={
    <ProtectedRoute subModuleId={20}>
      <Registro />
    </ProtectedRoute>
  } />
  
<Route path="/reporteria" element={
  <ProtectedRoute subModuleId={18}>
    <Box sx={{ width: '100vw', marginLeft: 'calc(50% - 50vw)', marginRight: 'calc(50% - 50vw)', minHeight: '100vh' }}>
      <Reporteria />
    </Box>
  </ProtectedRoute>
} />
  {/* New Route for ReporteAgrupado */}
  <Route path="/reporteagrupado" element={
    <ProtectedRoute subModuleId={18}> {/* Adjust subModuleId if different */}
     <Box sx={{ width: '500%',marginLeft: '-950px',minHeight: '90vh' }}>
      <ReporteAgrupado />
      </Box>
    </ProtectedRoute>
  } />
<Route path="/historico-representantes" element={
  <ProtectedRoute subModuleId={18}>
     <Box sx={{ width: '140%',marginLeft: '-230px',minHeight: '100vh' }}>
    <HistoricoRepresentantes />
    </Box>
  </ProtectedRoute>
} />
  <Route path="/categoria" element={
    <ProtectedRoute subModuleId={23}>
      <Categorias />
    </ProtectedRoute>
  } />
  
  <Route path="/registro-modular" element={
    <ProtectedRoute subModuleId={24}>
      <RegistroModular />
    </ProtectedRoute>
  } />
  
  <Route path="/EditarRepresentantes" element={
    <ProtectedRoute subModuleId={22}>
      <MatrizRepresentante />
    </ProtectedRoute>
  } />
  
  {/* Otras rutas protegidas */}
  <Route path="/sumatoria" element={
    <ProtectedRoute>
      <Sumatoria />
    </ProtectedRoute>
  } /> 
  
  <Route path="/unauthorized" element={<UnauthorizedAccess />} />
  
  {/* Ruta catch-all */}
  <Route path="*" element={<Navigate to="/" replace />} />
</Routes>

        </Container>
      </div>
    </ThemeProvider>
  );
};

const App = () => {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
};

export default App;